import {Component, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {DatastoreDto, GatewayPublicUserService, User, UserDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {AccountService} from '../../../auth/account.service';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

interface UserStatus {
    user: UserDTO;
    isConnected: boolean;
}

@Component({
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],
    providers: [MessageService],
    standalone: false
})

export class UserComponent implements OnInit {

    breadcrumb: MenuItem[] = [
        {label: this.translate.instant('menu.admin.label')},
        {label: this.translate.instant('menu.admin.users'), routerLink: '/h/admin/user-management'}] ;
    description: string = this.translate.instant('userList.description');
    datastore: DatastoreDto;

    users: UserDTO[];
    usersStatuses: UserStatus[];

    actual: UserDTO = null;
    authUser: User = null;

    showDetails = false;

    state: { message: string };

    items = [
        {
            label: this.translate.instant('userList.menu.view'),
            items: [{
                label: this.translate.instant('userList.menu.details'),
                icon: 'fas fa-user',
                permission: 'USERS.read',
                command: () => {
                    this.showDetails = true;
                }
            }]
        },
        {
            label: this.translate.instant('userList.menu.manage'),
            items: [{
                label: this.translate.instant('userList.menu.edit'),
                icon: 'fas fa-pen',
                permission: 'USERS.edit',
                command: () => {
                    this.router.navigate([`/h/admin/user-management/edit/${this.actual.id}/${this.actual.login}`]);
                }
            },
                {
                    label: this.translate.instant('userList.menu.delete'),
                    icon: 'fas fa-trash',
                    permission: 'USERS.delete',
                    command: () => {
                        this.confirmationService.confirm({
                            message: this.translate.instant('userList.confirm.delete', {login: this.actual.login}),
                            accept: () => {
                                // this.userService.deleteUserUsingDELETE(this.actual.login)
                                this.gatewayPublicUserService.deleteUser(this.actual.id).subscribe(() => {
                                    this.loadAll();
                                    this.toastSuccess('userList.toasts.deleted');
                                }, (e) => this.toastError(e, 'userList.toasts.deleteError'));
                            }
                        });
                    }
                },
                {
                    label: this.translate.instant('userList.menu.sendEmail'),
                    icon: 'fas fa-paper-plane',
                    permission: 'USERS.sendEmail',
                    command: () => {
                        this.gatewayPublicUserService.executeAction(this.actual.id).subscribe(() => {
                            this.toastSuccess('userList.toasts.emailSent');
                        }, (e) => this.toastError(e, 'userList.toasts.emailError'));
                    }
                }

            ]
        },
        {
        label: this.translate.instant('userList.menu.authentication'),
            items: [{
                label: this.translate.instant('userList.menu.logout'),
                permission: 'USERS.logOut',
                icon: 'fas fa-sign-out-alt',
                command: () => {
                    this.confirmLogout();
                }
            }]
        }
    ];

    itemsForOwnUser: MenuItem[] = this.items.slice(0, -2);

    constructor(
        private confirmationService: ConfirmationService,
        private accountService: AccountService,
        private messageService: MessageService,
        private router: Router,
        private gatewayPublicUserService: GatewayPublicUserService,
        private readonly translate: TranslateService) {
        const navigation = this.router.getCurrentNavigation();
        this.state = (navigation?.extras?.state as { message: string }) ?? null;
    }

    ngOnInit(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.accountService.identity().then((user: any) => this.authUser = user);
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.toastSuccess('userList.toasts.created'));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.toastSuccess('userList.toasts.updated'));
        }
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
    }

    loadAll() {
        console.log('load');
        const isAdmin = !(this.datastore && this.datastore.code && this.datastore.code.length > 0);
        this.gatewayPublicUserService.getAllUsers(this.datastore?.code, null, false).subscribe((users) => {
            users.filter(u => u.createdDate).forEach(u => u.createdDate = moment.unix(u.createdDate).format('DD/MM/YYYY HH:mm'));
            this.users = users;
            this.usersStatuses = [];
            const usersId = users.map(u => u.id);

            this.gatewayPublicUserService.getUsersStatuses(usersId).subscribe((usersStatusesObject) => {
                const usersStatuses = new Map<string, boolean>(Object.entries(usersStatusesObject));
                for (const user of users) {
                    const status: boolean = usersStatuses.get(user.id);
                    this.usersStatuses.push({user, isConnected: status});
                }
            });
        }, (e) => this.toastError(e, 'userList.toasts.loadError'));
    }

    confirmLogout() {
        this.confirmationService.confirm({
            message: this.translate.instant('userList.confirm.logout', {login: this.actual.login}),
            accept: () => {
                this.gatewayPublicUserService.logoutUser(this.actual.id).subscribe(() => {
                    this.loadAll();
                    this.toastSuccess('userList.toasts.loggedOut');
                }, (e) => this.toastError(e, 'userList.toasts.logoutError')
            );
            }
        });
    }

    navigateToDetail(userStatus: UserStatus) {
        this.actual = userStatus.user;
        this.showDetails = true;
    }

    private toastSuccess(detailKey: string) {
        this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('userList.toasts.successSummary'),
            detail: this.translate.instant(detailKey)
        });
    }

    // A server error with a title (account out of the admin's scope…) has already been shown by ErrorsInterceptor,
    // which then rethrows a string: only the errors it let through get a toast here.
    private toastError(e: unknown, detailKey: string) {
        if (e instanceof HttpErrorResponse) {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('userList.toasts.errorSummary'),
                detail: this.translate.instant(detailKey)
            });
        }
    }
}
